// frontend/src/components/WhatsAppTemplateSelectorModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-16
// Updated:      2026-09-16
//
// Description:
//   WhatsApp Template Selector modal (business-initiated messages).
//   • Opened from "New WhatsApp" actions (e.g. ContactMessagesAccordian).
//   • Lists available WhatsApp templates (DB-backed + provider-backed).
//   • Shows preview for the selected template.
//   • Detects placeholders and renders inputs.
//   • If DB list does not include variables/body, attempts detail route fetch.
//   • On confirm, calls onConfirm(...) with template + filled variables.
//
// IMPORTANT UI RULES (per Leon):
//   - Must use ModalWrapper (no portal, no duplicate close button)
//   - Must size to 80% width, 60% height (full size class + fixed height container)
//   - Must NOT regress contact resolving / template list / defaults
//
// FIX (2025-12-22):
//   - When opened from inside an existing WhatsApp thread modal, this selector was rendering BEHIND.
//   - Root cause: DOM order + z-index collisions between multiple modal systems.
//   - Solution: Create our own dedicated portal mount appended LAST to document.body,
//               render there, and force a max z-index wrapper.
//   - DOES NOT modify ModalWrapper (avoids regressions across the app).
//
// Responsive/readability pass (2026-09-16):
//   - Template rows use explicit theme backgrounds with white text throughout; selected rows use accent.
//   - Mobile modal is viewport-safe while desktop retains the existing 60vh layout.
//   - Template list is height-bounded and independently scrollable on mobile.
//   - Preview/variables stack vertically on mobile and retain the existing desktop split.
//   - Search, controls, variable inputs and footer are width-contained on narrow screens.
//   - No contact resolution, template loading, detail hydration, defaults or confirm payload logic changed.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaWhatsapp } from "react-icons/fa";
import ModalWrapper from "./ModalWrapper";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

// ─────────────────────────────────────────────────────────────
// Small utilities (safe + predictable)
// ─────────────────────────────────────────────────────────────
const safeStr = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));
const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

const titleCase = (s) => {
  const str = safeStr(s).trim();
  if (!str) return "";
  return str
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
};

// Use last 3 words from snake_case name, sentence case.
const friendlyFromSnakeLast3 = (name) => {
  const raw = safeStr(name).trim();
  if (!raw) return "-";
  const parts = raw.split("_").filter(Boolean);
  if (!parts.length) return "-";

  const filtered = parts.filter((p, idx) => {
    if (idx === 0 && ["pca", "whatsapp", "template"].includes(String(p).toLowerCase())) return false;
    return true;
  });

  const useParts = (filtered.length ? filtered : parts).slice(-3);
  const sentence = useParts.join(" ");
  return titleCase(sentence) || "-";
};

const extractPlaceholdersFromText = (text) => {
  // Supports {{1}}, {{ 1 }}, {{name}}, etc.
  const t = safeStr(text);
  if (!t) return [];
  const result = [];
  const regex = /{{\s*([^}]+)\s*}}/g;
  let match;
  while ((match = regex.exec(t)) !== null) {
    const key = safeStr(match[1]).trim();
    if (key && !result.includes(key)) result.push(key);
  }
  return result;
};

const dedupe = (arr) => {
  const out = [];
  const seen = new Set();
  (arr || []).forEach((x) => {
    const k = safeStr(x).trim();
    if (!k) return;
    if (seen.has(k)) return;
    seen.add(k);
    out.push(k);
  });
  return out;
};

// ─────────────────────────────────────────────────────────────
// Normalisers (DB vs Provider shapes)
// ─────────────────────────────────────────────────────────────
const normaliseDbTemplate = (t) => {
  if (!t) return null;

  const dbId = t.id ?? null;
  const contentSid = t.content_sid || t.template_sid || t.sid || null;

  const name =
    t.name ||
    t.template_name ||
    t.friendly_name ||
    contentSid ||
    dbId ||
    "Unnamed template";

  const preview =
    t.body_preview ||
    t.body_text ||
    t.sample_body ||
    t.body ||
    t.preview ||
    "";

  const varsSchema = Array.isArray(t.variables_schema) ? t.variables_schema : [];

  const schemaKeys = varsSchema
    .map((v) => safeStr(v?.key || v?.label).trim())
    .filter(Boolean);

  const tokenKeys = extractPlaceholdersFromText(preview);

  const placeholders = dedupe([...schemaKeys, ...tokenKeys]);

  return {
    raw: t,
    source: "db",
    id: String(dbId ?? contentSid ?? name),
    db_id: dbId,
    content_sid: contentSid,
    name: safeStr(name),
    friendly_name: friendlyFromSnakeLast3(name),
    category: t.provider_category_label || t.category || "TWILIO/TEXT",
    provider_status: t.provider_status || t.status || "",
    language: t.language || t?.provider_payload?.provider_language || "",
    preview: safeStr(preview),
    variables_schema: varsSchema,
    placeholders,
  };
};

const normaliseProviderTemplate = (t) => {
  if (!t) return null;

  const sid = t.id || t.template_id || t.sid || null;

  const name =
    t.name ||
    t.template_name ||
    t.friendly_name ||
    sid ||
    "Unnamed template";

  const preview =
    t.preview ||
    t.body_text ||
    t.sample_body ||
    t.body ||
    "";

  const placeholders = Array.isArray(t.placeholders)
    ? dedupe(t.placeholders.map((x) => safeStr(x).trim()))
    : extractPlaceholdersFromText(preview);

  return {
    raw: t,
    source: "provider",
    id: String(sid ?? name),
    db_id: null,
    content_sid: sid,
    name: safeStr(name),
    friendly_name: safeStr(t.friendly_name || "") || friendlyFromSnakeLast3(name),
    category: t.category || t.type || t.purpose || "TWILIO/TEXT",
    provider_status: t.provider_status || t.status || "",
    language: t.language || "",
    preview: safeStr(preview),
    variables_schema: [],
    placeholders,
  };
};

const mergeTemplates = (dbTpl, providerTpl) => {
  const base = dbTpl || providerTpl;
  if (!base) return null;

  const merged = { ...base };

  if (dbTpl) {
    merged.source = providerTpl ? "db+provider" : "db";
    merged.raw = { db: dbTpl.raw, provider: providerTpl?.raw || null };
    merged.db_id = dbTpl.db_id;
    merged.content_sid = dbTpl.content_sid || providerTpl?.content_sid || null;
    merged.name = dbTpl.name;
    merged.friendly_name = dbTpl.friendly_name;
    merged.preview = dbTpl.preview || providerTpl?.preview || "";
    merged.variables_schema = Array.isArray(dbTpl.variables_schema) ? dbTpl.variables_schema : [];
    merged.placeholders = dedupe([
      ...(dbTpl.placeholders || []),
      ...((providerTpl && providerTpl.placeholders) || []),
    ]);
    merged.category = dbTpl.category || providerTpl?.category || "TWILIO/TEXT";
    merged.provider_status = providerTpl?.provider_status || dbTpl.provider_status || "";
    merged.language = dbTpl.language || providerTpl?.language || "";
    return merged;
  }

  merged.source = "provider";
  return merged;
};

// ─────────────────────────────────────────────────────────────
// Variable defaulting heuristics
// ─────────────────────────────────────────────────────────────
const pickDefaultForVar = ({ key, label }, ctx) => {
  const k = safeStr(key).toLowerCase();
  const l = safeStr(label).toLowerCase();

  const wantName = k.includes("name") || l.includes("name");
  const wantJob = k.includes("job") || k.includes("ref") || l.includes("job") || l.includes("ref");
  const wantPhone =
    k.includes("phone") || k.includes("mobile") || k.includes("tel") ||
    l.includes("phone") || l.includes("mobile") || l.includes("tel");

  if (wantName) return ctx.contact_name || "";
  if (wantJob) return ctx.job_ref || "";
  if (wantPhone) return ctx.contact_phone || "";
  return "";
};

// ─────────────────────────────────────────────────────────────
// Detail fetch helpers (best-effort)
// ─────────────────────────────────────────────────────────────
const parseDetailPayloadIntoTemplate = (tpl, data) => {
  if (!tpl || !data) return tpl;

  const obj =
    data?.item && isObj(data.item) ? data.item :
    data?.template && isObj(data.template) ? data.template :
    data;

  const next = { ...tpl };

  const preview =
    obj.body_preview ||
    obj.body_text ||
    obj.sample_body ||
    obj.body ||
    obj.preview ||
    next.preview ||
    "";

  const schema =
    Array.isArray(obj.variables_schema) ? obj.variables_schema :
    Array.isArray(obj.variables) ? obj.variables :
    Array.isArray(obj.schema) ? obj.schema :
    Array.isArray(obj.placeholders_schema) ? obj.placeholders_schema :
    next.variables_schema ||
    [];

  const schemaKeys = Array.isArray(schema)
    ? schema.map((v) => safeStr(v?.key || v?.label).trim()).filter(Boolean)
    : [];

  const tokenKeys = extractPlaceholdersFromText(preview);
  const providerKeys = Array.isArray(obj.placeholders) ? obj.placeholders : [];

  next.preview = safeStr(preview);
  next.variables_schema = Array.isArray(schema) ? schema : [];
  next.placeholders = dedupe([
    ...(next.placeholders || []),
    ...schemaKeys,
    ...tokenKeys,
    ...providerKeys.map((x) => safeStr(x).trim()),
  ]);

  return next;
};

const fetchTemplateDetailBestEffort = async ({ tenantId, tpl }) => {
  const sid = tpl?.content_sid ? String(tpl.content_sid) : null;
  const dbId = tpl?.db_id != null ? String(tpl.db_id) : null;

  const candidates = [];

  if (dbId) {
    candidates.push({ url: `/api/messaging/templates/whatsapp/${dbId}`, params: { tenant_id: tenantId } });
    candidates.push({ url: `/api/messaging/templates/whatsapp/detail/${dbId}`, params: { tenant_id: tenantId } });
  }
  if (sid) {
    candidates.push({ url: `/api/messaging/templates/whatsapp/${sid}`, params: { tenant_id: tenantId } });
    candidates.push({ url: `/api/messaging/whatsapp/templates/${sid}`, params: { tenant_id: tenantId } });
    candidates.push({ url: `/api/messaging/whatsapp/templates/detail/${sid}`, params: { tenant_id: tenantId } });
  }

  if (!candidates.length) return null;

  for (const c of candidates) {
    try {
      const res = await api.get(c.url, { params: c.params });
      if (res?.data) return res.data;
    } catch (e) {
      // keep trying
    }
  }
  return null;
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────
const WhatsAppTemplateSelectorModal = ({
  open,
  onClose,
  onConfirm,

  contactId = null,
  contactPhone = "",
  contactName = "",

  jobId = null,
  jobRef = "",

  templates: templatesProp = null,

  tenantId = 1,
}) => {
  const { theme } = useTheme();

  // Dedicated portal mount node appended LAST to body (prevents modal-behind-modal)
  const [portalEl, setPortalEl] = useState(null);

  useEffect(() => {
    if (!open) return;

    const el = document.createElement("div");
    el.setAttribute("data-modal", "whatsapp-template-selector");
    // Append LAST => wins DOM order ties when z-index matches.
    document.body.appendChild(el);
    setPortalEl(el);

    return () => {
      try {
        document.body.removeChild(el);
      } catch (e) {
        // ignore
      }
      setPortalEl(null);
    };
  }, [open]);

  // Data
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  // Selection
  const [selectedId, setSelectedId] = useState(null);

  // Search/filter
  const [query, setQuery] = useState("");
  const [showOnlyWithVars, setShowOnlyWithVars] = useState(false);

  // Variable values + detail loading state
  const [variableValues, setVariableValues] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailNote, setDetailNote] = useState("");

  // Lock context at open time
  const [lockedContact, setLockedContact] = useState({ id: null, phone: "", name: "" });
  const [lockedJob, setLockedJob] = useState({ id: null, ref: "" });

  // Refs for focusing first variable input
  const firstVarRef = useRef(null);

  const accent = theme?.accent_color || "var(--accent-color)" || "#ff7a00";
  const primary =
    theme?.primary_color ||
    theme?.secondary_color ||
    "#4a6a88";

  useEffect(() => {
    if (!open) return;

    setSelectedId(null);
    setVariableValues({});
    setQuery("");
    setShowOnlyWithVars(false);
    setDetailNote("");
    setDetailLoading(false);

    setLockedContact({
      id: contactId ?? null,
      phone: safeStr(contactPhone).trim(),
      name: safeStr(contactName).trim(),
    });

    setLockedJob({
      id: jobId ?? null,
      ref: safeStr(jobRef).trim(),
    });
  }, [open, contactId, contactPhone, contactName, jobId, jobRef]);

  useEffect(() => {
    if (!open) return;
    if (!lockedContact?.id) return;
    if (lockedContact.name && lockedContact.phone) return;

    let cancelled = false;

    const loadContact = async () => {
      try {
        const cid = lockedContact.id;
        const endpoints = [`/api/contacts/${cid}`, `/api/contact/${cid}`];

        let data = null;
        for (const ep of endpoints) {
          try {
            const res = await api.get(ep);
            data = res?.data || null;
            if (data) break;
          } catch (e) {
            // try next
          }
        }

        if (cancelled || !data) return;

        const first = safeStr(data.first_name || data.firstname).trim();
        const last = safeStr(data.last_name || data.lastname).trim();
        const fullName = `${first} ${last}`.trim() || safeStr(data.name).trim();

        const phone = safeStr(
          data.phone_number || data.mobile_number || data.whatsapp || data.phone
        ).trim();

        setLockedContact((prev) => ({
          ...prev,
          name: prev.name?.trim() ? prev.name : fullName,
          phone: prev.phone?.trim() ? prev.phone : phone,
        }));
      } catch (e) {
        // silent
      }
    };

    loadContact();
    return () => {
      cancelled = true;
    };
  }, [open, lockedContact?.id]);

  useEffect(() => {
    if (!open) return;

    if (Array.isArray(templatesProp) && templatesProp.length) {
      const normalised = templatesProp.map(normaliseDbTemplate).filter(Boolean);
      setTemplates(normalised);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadErr("");
      try {
        const [dbRes, providerRes] = await Promise.allSettled([
          api.get("/api/messaging/templates/whatsapp", {
            params: { tenant_id: tenantId, include_inactive: false },
          }),
          api.get("/api/messaging/whatsapp/templates", {
            params: { tenant_id: tenantId },
          }),
        ]);

        if (cancelled) return;

        const dbRows =
          dbRes.status === "fulfilled" && Array.isArray(dbRes.value?.data)
            ? dbRes.value.data
            : [];

        const providerRows =
          providerRes.status === "fulfilled" && Array.isArray(providerRes.value?.data)
            ? providerRes.value.data
            : [];

        const dbNorm = dbRows.map(normaliseDbTemplate).filter(Boolean);
        const provNorm = providerRows.map(normaliseProviderTemplate).filter(Boolean);

        const provBySid = new Map();
        provNorm.forEach((p) => {
          const sid = p?.content_sid ? String(p.content_sid) : null;
          if (sid) provBySid.set(sid, p);
        });

        const merged = dbNorm.map((d) => {
          const sid = d?.content_sid ? String(d.content_sid) : null;
          const p = sid ? provBySid.get(sid) : null;
          return mergeTemplates(d, p);
        });

        const dbSidSet = new Set(
          dbNorm.map((d) => (d?.content_sid ? String(d.content_sid) : null)).filter(Boolean)
        );

        provNorm.forEach((p) => {
          const sid = p?.content_sid ? String(p.content_sid) : null;
          if (!sid) return;
          if (dbSidSet.has(sid)) return;
          merged.push(p);
        });

        setTemplates(merged.filter(Boolean));

        if (!merged.length) {
          setLoadErr("No WhatsApp templates returned. Check Twilio sync and messaging settings.");
        }
      } catch (e) {
        if (cancelled) return;
        console.error("[WhatsAppTemplateSelectorModal] template load failed", e);
        setLoadErr("Failed to load WhatsApp templates. Check backend logs and messaging settings.");
        setTemplates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, templatesProp, tenantId]);

  const selectedTemplate = useMemo(() => {
    if (!selectedId) return null;
    return templates.find((t) => String(t.id) === String(selectedId)) || null;
  }, [templates, selectedId]);

  const filteredTemplates = useMemo(() => {
    const q = safeStr(query).trim().toLowerCase();
    const base = templates || [];

    const filtered = base.filter((t) => {
      if (!t) return false;

      if (showOnlyWithVars) {
        const hasVars =
          (Array.isArray(t.variables_schema) && t.variables_schema.length > 0) ||
          (Array.isArray(t.placeholders) && t.placeholders.length > 0);
        if (!hasVars) return false;
      }

      if (!q) return true;

      const hay = [t.name, t.friendly_name, t.content_sid, t.category, t.preview]
        .map((x) => safeStr(x).toLowerCase())
        .join(" | ");

      return hay.includes(q);
    });

    return filtered.sort((a, b) => {
      const aVars = (a?.placeholders?.length || 0) + (a?.variables_schema?.length || 0);
      const bVars = (b?.placeholders?.length || 0) + (b?.variables_schema?.length || 0);
      if (aVars !== bVars) return bVars - aVars;
      return safeStr(a?.friendly_name || a?.name).localeCompare(safeStr(b?.friendly_name || b?.name));
    });
  }, [templates, query, showOnlyWithVars]);

  useEffect(() => {
    if (!open) return;

    const run = async () => {
      if (!selectedTemplate) {
        setVariableValues({});
        setDetailNote("");
        setDetailLoading(false);
        return;
      }

      setDetailNote("");
      setDetailLoading(false);

      const hasSchema =
        Array.isArray(selectedTemplate.variables_schema) &&
        selectedTemplate.variables_schema.length > 0;

      const hasPlaceholders =
        Array.isArray(selectedTemplate.placeholders) &&
        selectedTemplate.placeholders.length > 0;

      if (!hasSchema || !hasPlaceholders || !selectedTemplate.preview) {
        setDetailLoading(true);
        const detail = await fetchTemplateDetailBestEffort({
          tenantId,
          tpl: selectedTemplate,
        });
        setDetailLoading(false);

        if (detail) {
          const hydrated = parseDetailPayloadIntoTemplate(selectedTemplate, detail);

          setTemplates((prev) =>
            (prev || []).map((t) => (String(t.id) === String(selectedTemplate.id) ? hydrated : t))
          );

          setDetailNote("Template details loaded (body/variables).");
        } else {
          setDetailNote("No detail route returned variables/body for this template.");
        }
      }

      const ctx = {
        contact_name: lockedContact.name,
        contact_phone: lockedContact.phone,
        job_ref: lockedJob.ref,
      };

      const schema = Array.isArray(selectedTemplate.variables_schema)
        ? selectedTemplate.variables_schema
        : [];

      const keysFromSchema = schema
        .map((v, idx) => {
          const k = safeStr(v?.key || v?.label).trim();
          return k || `var_${idx + 1}`;
        })
        .filter(Boolean);

      const keysFromTokens = Array.isArray(selectedTemplate.placeholders)
        ? selectedTemplate.placeholders
        : [];

      const finalKeys = dedupe([...keysFromSchema, ...keysFromTokens]);

      setVariableValues((prev) => {
        const next = {};
        finalKeys.forEach((k) => {
          if (prev && prev[k] !== undefined) {
            next[k] = prev[k];
            return;
          }
          const schemaItem = schema.find((v) => safeStr(v?.key).trim() === k) || null;
          const label = schemaItem ? safeStr(schemaItem?.label) : k;
          next[k] = pickDefaultForVar({ key: k, label }, ctx);
        });
        return next;
      });

      setTimeout(() => {
        if (firstVarRef.current) firstVarRef.current.focus();
      }, 50);
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const handleVariableChange = (key, value) => {
    setVariableValues((prev) => ({ ...(prev || {}), [key]: value }));
  };

  const renderPreviewText = () => {
    if (!selectedTemplate) return "";
    let text = safeStr(selectedTemplate.preview);

    Object.entries(variableValues || {}).forEach(([key, value]) => {
      const re = new RegExp(`{{\\s*${key}\\s*}}`, "gi");
      text = text.replace(re, safeStr(value));
    });

    return text;
  };

  const handleConfirm = () => {
    if (!selectedTemplate || !onConfirm) return;

    const payload = {
      channel: "whatsapp",
      tenant_id: tenantId,

      contact_id: lockedContact.id,
      contact_phone: lockedContact.phone,
      contact_name: lockedContact.name,

      job_id: lockedJob.id,
      job_ref: lockedJob.ref,

      template_db_id: selectedTemplate.db_id ?? null,
      template_content_sid: selectedTemplate.content_sid ?? null,
      template_id: selectedTemplate.content_sid || selectedTemplate.db_id || selectedTemplate.id,

      template_name: selectedTemplate.name,
      template_friendly_name: selectedTemplate.friendly_name,
      template_category: selectedTemplate.category,
      template_language: selectedTemplate.language,

      variables_schema: Array.isArray(selectedTemplate.variables_schema) ? selectedTemplate.variables_schema : [],
      placeholders: { ...(variableValues || {}) },

      body_text: renderPreviewText(),

      raw_template: selectedTemplate.raw,
      source: "template_selector",
    };

    onConfirm(payload);
  };

  if (!open) return null;

  const hasVars =
    selectedTemplate &&
    (
      (Array.isArray(selectedTemplate.variables_schema) && selectedTemplate.variables_schema.length > 0) ||
      (Array.isArray(selectedTemplate.placeholders) && selectedTemplate.placeholders.length > 0)
    );

  // Don’t render until our portal mount is ready.
  if (!portalEl) return null;

  const content = (
    <div
      // MAX z-index so nothing in your app can sit above it.
      style={{ position: "fixed", inset: 0, zIndex: 2147483647 }}
    >
      <ModalWrapper onClose={onClose} size="full">
        <div className="flex h-[82dvh] w-full min-w-0 flex-col overflow-hidden rounded-xl bg-white shadow-2xl md:h-[60vh] md:rounded-2xl">
          {/* Header */}
          <div className="flex shrink-0 flex-col gap-2 border-b border-gray-200 bg-[#075E54] px-3 py-3 text-white sm:flex-row sm:items-center sm:justify-between sm:px-5 md:rounded-t-2xl">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#25D366]">
                <FaWhatsapp />
              </div>
              <div className="min-w-0 flex flex-col">
                <span className="break-words text-sm font-semibold">WhatsApp Template Selector</span>
                <span className="break-words text-[11px] opacity-80">
                  Business-initiated message – choose a Meta-approved template
                </span>
              </div>
            </div>

            <div className="break-words text-left text-[11px] opacity-80 sm:text-right">
              {lockedContact.name || lockedContact.phone ? (
                <div>
                  Target: {lockedContact.name || "Contact"}
                  {lockedContact.phone ? ` (${lockedContact.phone})` : ""}
                </div>
              ) : lockedContact.id ? (
                <div>Target: Contact ID {lockedContact.id}</div>
              ) : (
                <div>No contact selected</div>
              )}
              {lockedJob.ref ? <div>Job: {lockedJob.ref}{lockedJob.id ? ` (ID ${lockedJob.id})` : ""}</div> : null}
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 flex-col overflow-y-auto md:flex md:flex-row md:overflow-hidden">
            {/* List */}
            <div className="flex w-full shrink-0 flex-col overflow-hidden border-b border-gray-200 md:min-h-0 md:w-2/5 md:shrink md:border-b-0 md:border-r">
              <div className="shrink-0 border-b bg-gray-50 px-3 py-3 sm:px-4">
                <div className="break-words text-sm font-semibold text-gray-800">Templates</div>
                <div className="break-all text-xs text-gray-600">
                  DB: <span style={{ fontFamily: "monospace" }}>/api/messaging/templates/whatsapp</span>{" "}
                  • Provider: <span style={{ fontFamily: "monospace" }}>/api/messaging/whatsapp/templates</span>
                </div>

                <div className="mt-3 flex min-w-0 items-center gap-2">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search templates…"
                    className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none"
                  />

                  <button
                    type="button"
                    onClick={() => setShowOnlyWithVars((v) => !v)}
                    className="shrink-0 rounded border px-2 py-1 text-xs font-semibold"
                    style={{
                      borderColor: accent,
                      color: showOnlyWithVars ? "#fff" : accent,
                      background: showOnlyWithVars ? accent : "transparent",
                    }}
                    title="Only show templates that have variables/placeholders"
                  >
                    Vars only
                  </button>
                </div>
              </div>

              {loading && <div className="px-4 py-3 text-xs text-gray-600">Loading templates…</div>}
              {loadErr && !loading && <div className="px-4 py-3 text-xs text-red-600">❌ {loadErr}</div>}

              <div className="max-h-[32dvh] overflow-y-auto md:min-h-0 md:max-h-none md:flex-1">
                {!loading && !loadErr && filteredTemplates.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-gray-600">No templates match your search/filter.</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredTemplates.map((t) => {
                      const isSelected = String(t.id) === String(selectedId);

                      const varCount =
                        (Array.isArray(t.variables_schema) ? t.variables_schema.length : 0) ||
                        (Array.isArray(t.placeholders) ? t.placeholders.length : 0);

                      const badge =
                        varCount > 0 ? `${varCount} VARS` : (t.source === "provider" ? "PROVIDER" : "NO VARS");

                      return (
                        <button
                          key={String(t.id)}
                          type="button"
                          onClick={() => setSelectedId(t.id)}
                          className={`w-full px-3 py-3 text-left text-sm transition-[filter] hover:brightness-95 sm:px-4 ${
                            isSelected ? "border-l-4" : ""
                          }`}
                          style={{
                            background: isSelected ? accent : primary,
                            borderLeftColor: isSelected ? accent : primary,
                            color: "#ffffff",
                          }}
                          aria-pressed={isSelected}
                        >
                          <div className="flex min-w-0 items-start justify-between gap-2">
                            <span
                              className="min-w-0 break-words font-semibold"
                              style={{ color: "#ffffff" }}
                            >
                              {t.name}
                            </span>

                            <span
                              className="shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide"
                              style={{
                                background: "rgba(255,255,255,0.18)",
                                color: "#ffffff",
                              }}
                            >
                              {badge}
                            </span>
                          </div>

                          <div
                            className="mt-1 line-clamp-2 break-words text-[11px]"
                            style={{
                              color: "rgba(255,255,255,0.92)",
                            }}
                          >
                            {t.preview ? t.preview : "(no body text returned for this template)"}
                          </div>

                          <div
                            className="mt-1 flex min-w-0 items-center justify-between gap-2 text-[10px]"
                            style={{
                              color: "rgba(255,255,255,0.80)",
                            }}
                          >
                            <span className="min-w-0 break-words">
                              {t.category || "TWILIO/TEXT"}
                            </span>
                            <span
                              className="shrink-0"
                              style={{ fontFamily: "monospace" }}
                            >
                              {t.content_sid ? String(t.content_sid).slice(-10) : ""}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Right */}
            <div className="flex w-full shrink-0 flex-col md:min-h-0 md:w-3/5 md:shrink md:overflow-hidden">
              <div className="flex shrink-0 flex-col gap-0.5 border-b bg-gray-50 px-3 py-3 sm:px-4">
                <div className="text-sm font-semibold text-gray-800">
                  {selectedTemplate ? selectedTemplate.name : "Select a template"}
                </div>

                <div className="text-[11px] text-gray-600">
                  {selectedTemplate ? (
                    <>
                      Source: <span style={{ fontFamily: "monospace" }}>{selectedTemplate.source}</span>
                      {selectedTemplate.content_sid ? (
                        <>
                          {" "}• SID: <span style={{ fontFamily: "monospace" }}>{selectedTemplate.content_sid}</span>
                        </>
                      ) : null}
                    </>
                  ) : (
                    "Choose a template on the left to see preview and variables."
                  )}
                </div>

                {detailLoading ? (
                  <div className="text-[11px] text-gray-600">Loading template details…</div>
                ) : detailNote ? (
                  <div className="text-[11px] text-gray-600">{detailNote}</div>
                ) : null}
              </div>

              <div className="flex flex-col md:min-h-0 md:flex-1 md:flex-row md:overflow-hidden">
                {/* Preview */}
                <div className="w-full border-b border-gray-200 p-3 sm:p-4 md:w-3/5 md:overflow-auto md:border-b-0 md:border-r">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Message preview</div>

                  <div className="min-h-[120px] whitespace-pre-wrap break-words rounded-lg bg-[#e5ddd5] px-3 py-2 text-sm [overflow-wrap:anywhere]">
                    {selectedTemplate ? (
                      renderPreviewText()
                    ) : (
                      <span className="text-gray-500 text-xs">
                        Choose a template on the left to see the message preview.
                      </span>
                    )}
                  </div>

                  {selectedTemplate ? (
                    <div className="mt-2 text-[10px] text-gray-500">
                      Placeholders are replaced locally for preview. Backend still sends template + variables.
                    </div>
                  ) : null}
                </div>

                {/* Variables */}
                <div className="w-full p-3 sm:p-4 md:w-2/5 md:overflow-auto">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Placeholder values</div>

                  {selectedTemplate ? (
                    hasVars ? (
                      <div className="space-y-2">
                        {(
                          Array.isArray(selectedTemplate.variables_schema) &&
                          selectedTemplate.variables_schema.length > 0
                            ? selectedTemplate.variables_schema.map((v, idx) => {
                                const key = safeStr(v?.key || v?.label).trim() || `var_${idx + 1}`;
                                const label = safeStr(v?.label || v?.key).trim() || key;

                                return (
                                  <div key={key} className="flex flex-col gap-1">
                                    <label className="text-[11px] font-semibold text-gray-700">
                                      {label}
                                      {v?.key && v?.label && v.key !== v.label ? (
                                        <span className="text-[10px] text-gray-500" style={{ marginLeft: 6 }}>
                                          ({v.key})
                                        </span>
                                      ) : null}
                                    </label>
                                    <input
                                      ref={idx === 0 ? firstVarRef : null}
                                      type="text"
                                      className="w-full min-w-0 rounded border border-gray-300 px-2 py-1 text-sm focus:border-[#25D366] focus:outline-none"
                                      value={safeStr(variableValues[key])}
                                      onChange={(e) => handleVariableChange(key, e.target.value)}
                                      placeholder="Enter value"
                                    />
                                  </div>
                                );
                              })
                            : (selectedTemplate.placeholders || []).map((ph, idx) => (
                                <div key={ph} className="flex flex-col gap-1">
                                  <label className="text-[11px] font-semibold text-gray-700">{`{{${ph}}}`}</label>
                                  <input
                                    ref={idx === 0 ? firstVarRef : null}
                                    type="text"
                                    className="w-full min-w-0 rounded border border-gray-300 px-2 py-1 text-sm focus:border-[#25D366] focus:outline-none"
                                    value={safeStr(variableValues[ph])}
                                    onChange={(e) => handleVariableChange(ph, e.target.value)}
                                    placeholder="Enter value"
                                  />
                                </div>
                              ))
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">No placeholders/variables detected.</div>
                    )
                  ) : (
                    <div className="text-xs text-gray-500">Select a template first.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 flex-col gap-2 border-t border-gray-200 bg-gray-50 px-3 py-3 sm:px-5 md:flex-row md:items-center md:justify-between md:rounded-b-2xl">
            <div className="break-words text-[11px] text-gray-600">
              Business-initiated WhatsApp messages must use approved templates.
            </div>

            <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto md:items-center">
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded bg-btnGeneralNormal px-4 py-2 font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:flex-1 md:w-auto md:flex-none"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirm}
                disabled={!selectedTemplate}
                className="w-full rounded bg-btnActionNormal px-4 py-2 font-semibold text-white shadow hover:bg-btnActionHover active:bg-btnActionHover sm:flex-1 md:w-auto md:flex-none"
                style={{
                  opacity: selectedTemplate ? 1 : 0.6,
                  cursor: selectedTemplate ? "pointer" : "not-allowed",
                }}
                title={!selectedTemplate ? "Select a template first" : "Use selected template"}
              >
                <span className="inline-flex items-center gap-2">
                  <FaWhatsapp />
                  Use Template
                </span>
              </button>
            </div>
          </div>
        </div>
      </ModalWrapper>
    </div>
  );

  return createPortal(content, portalEl);
};

export default WhatsAppTemplateSelectorModal;
